/**
 * AUTHORITY — Shared operational-shell presenters (SSOT).
 *
 * PageIdentity · EmptyState · FailureState · DeniedState · WaitingState ·
 * PageGrammarSummary are the canonical UI for page identity and
 * empty/failure/denied/waiting phases.
 * Plane wrappers must adopt these (compatibility wrappers OK); do not invent
 * competing failure/denied/waiting primitives.
 *
 * SurfaceRuntimeFrame (src/shared/surface-runtime) is the companion frame authority.
 * Empty chrome may remain plane-local while still binding to the EmptyState grammar slot.
 *
 * Operational grammar primitives (ContextBar … RecoveryPanel) live in ./primitives
 * and are exported from the package index — do not rewrite them here.
 * PageIdentity / EmptyState / Failure / Denied / Waiting remain SSOT here.
 */

import React from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import {
  isBannedAmbiguousPrimaryLabel,
  resolveActionLabelKo,
  type CanonicalActionFamilyV0,
} from './actionGrammar';
import {
  isCompleteFailureExplanation,
  type FailureExplanationV0,
  type RecoveryActionBindingV0,
} from './recoveryGrammar';
import type { PageGrammarPayloadV0 } from './shellComposition';
import { isCompletePageGrammar } from './shellComposition';
import { OPERATION_STATE_TOKEN_MAP_V0, type CanonicalOperationStateV0 } from './stateGrammar';

const { Text, Paragraph, Title } = Typography;

/** Canonical presenter symbols — adoption / ratchet SSOT list. */
export const CANONICAL_PRESENTER_AUTHORITY_V0 = [
  'PageIdentity',
  'EmptyState',
  'FailureState',
  'DeniedState',
  'WaitingState',
  'PageGrammarSummary',
  'SurfaceRuntimeFrame',
] as const;

export type CanonicalPresenterAuthorityV0 = (typeof CANONICAL_PRESENTER_AUTHORITY_V0)[number];

/**
 * Thin adapter: build a complete FailureExplanationV0 from a short title/detail pair.
 * Plane wrappers should prefer this over inventing partial error copy.
 */
export function buildFailureExplanationV0(input: {
  whatHappened: string;
  whyItHappened: string;
  whatChanged?: string;
  whatDidNotChange?: string;
  whatCanHappenNext?: string;
  receiptOrSupportReference: string;
}): FailureExplanationV0 {
  return {
    whatHappened: input.whatHappened,
    whyItHappened: input.whyItHappened,
    whatChanged: input.whatChanged ?? '화면에 표시할 데이터가 갱신되지 않았습니다.',
    whatDidNotChange:
      input.whatDidNotChange ?? '서버의 업무 데이터·권한·증빙 상태는 변경되지 않았습니다.',
    whatCanHappenNext:
      input.whatCanHappenNext ?? '다시 시도하거나 지원 참조와 함께 운영자에게 문의하세요.',
    receiptOrSupportReference: input.receiptOrSupportReference,
  };
}

export function buildRetryActionBindingV0(
  runtimeOperationId: string,
  labelKo = '다시 시도'
): RecoveryActionBindingV0 {
  return {
    kind: 'retry',
    labelKo,
    runtimeOperationId,
    available: true,
    fakeUndo: false,
  };
}

export interface PageIdentityProps {
  areaLabel: string;
  title: string;
  state: CanonicalOperationStateV0;
  responsibility?: string;
  'data-testid'?: string;
}

export function PageIdentity(props: PageIdentityProps): React.ReactElement {
  const token = OPERATION_STATE_TOKEN_MAP_V0[props.state];
  return (
    <header data-testid={props['data-testid'] ?? 'page-identity'} aria-label="페이지 식별">
      <Text type="secondary">{props.areaLabel}</Text>
      <Title
        level={3}
        style={{
          marginTop: 'var(--page-identity-title-margin-block, var(--space-1))',
          marginBottom: 'var(--page-identity-title-margin-block, var(--space-1))',
        }}
      >
        {props.title}
      </Title>
      <Space size="middle">
        <Text>
          상태: <strong data-state-token={token.cssVar}>{token.labelKo}</strong>
        </Text>
        {props.responsibility ? <Text type="secondary">{props.responsibility}</Text> : null}
      </Space>
    </header>
  );
}

export interface EmptyStateProps {
  title?: string;
  description?: string;
  nextHint?: string;
  'data-testid'?: string;
  className?: string;
}

/** Thin EmptyState presenter — grammar slot SSOT; plane-local chrome may still wrap. */
export function EmptyState(props: EmptyStateProps): React.ReactElement {
  const alert = (
    <Alert
      type="info"
      showIcon
      data-testid="empty-state"
      className={props.className}
      message={props.title ?? '표시할 항목이 없습니다'}
      description={
        <Space direction="vertical" size="small">
          {props.description ? (
            <Paragraph style={{ marginBottom: 0 }}>{props.description}</Paragraph>
          ) : null}
          {props.nextHint ? (
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {props.nextHint}
            </Paragraph>
          ) : null}
        </Space>
      }
    />
  );
  if (props['data-testid']) {
    return <div data-testid={props['data-testid']}>{alert}</div>;
  }
  return alert;
}

export interface FailureStateProps {
  explanation: FailureExplanationV0;
  actions?: readonly RecoveryActionBindingV0[];
  onAction?: (kind: RecoveryActionBindingV0['kind']) => void;
  /** Outer wrapper test id for plane compatibility wrappers. */
  'data-testid'?: string;
  className?: string;
}

export function FailureState(props: FailureStateProps): React.ReactElement {
  if (!isCompleteFailureExplanation(props.explanation)) {
    throw new Error('FailureState requires a complete FailureExplanationV0');
  }
  const { explanation, actions = [] } = props;
  const alert = (
    <Alert
      type="error"
      showIcon
      data-testid="failure-state"
      className={props.className}
      message={explanation.whatHappened}
      description={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>원인</strong>: {explanation.whyItHappened}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>변경됨</strong>: {explanation.whatChanged}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>변경되지 않음</strong>: {explanation.whatDidNotChange}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>다음</strong>: {explanation.whatCanHappenNext}
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            참조: {explanation.receiptOrSupportReference}
          </Paragraph>
          {actions.length > 0 ? (
            <Space wrap>
              {actions
                .filter((a) => a.available)
                .map((action) => (
                  <Button
                    key={action.kind}
                    onClick={() => props.onAction?.(action.kind)}
                    disabled={!action.runtimeOperationId}
                  >
                    {action.labelKo}
                  </Button>
                ))}
            </Space>
          ) : null}
        </Space>
      }
    />
  );
  if (props['data-testid']) {
    return <div data-testid={props['data-testid']}>{alert}</div>;
  }
  return alert;
}

export interface DeniedStateProps {
  reason: string;
  requiredAuthority: string;
  whatDidNotChange: string;
  nextActionLabel: CanonicalActionFamilyV0;
  onNext?: () => void;
  'data-testid'?: string;
  className?: string;
}

export function DeniedState(props: DeniedStateProps): React.ReactElement {
  const label = resolveActionLabelKo(props.nextActionLabel);
  if (isBannedAmbiguousPrimaryLabel(label)) {
    throw new Error(`DeniedState primary action uses banned label: ${label}`);
  }
  const alert = (
    <Alert
      type="warning"
      showIcon
      data-testid="denied-state"
      className={props.className}
      message="권한이 부족하여 진행할 수 없습니다"
      description={
        <Space direction="vertical" size="small">
          <Paragraph style={{ marginBottom: 0 }}>{props.reason}</Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>필요 권한</strong>: {props.requiredAuthority}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>변경되지 않음</strong>: {props.whatDidNotChange}
          </Paragraph>
          {props.onNext ? (
            <Button type="primary" onClick={props.onNext}>
              {label}
            </Button>
          ) : null}
        </Space>
      }
    />
  );
  if (props['data-testid']) {
    return <div data-testid={props['data-testid']}>{alert}</div>;
  }
  return alert;
}

export interface WaitingStateProps {
  waitingReason: string;
  required: string;
  nextActionLabel: CanonicalActionFamilyV0;
  onNext?: () => void;
  'data-testid'?: string;
  className?: string;
}

export function WaitingState(props: WaitingStateProps): React.ReactElement {
  const label = resolveActionLabelKo(props.nextActionLabel);
  const alert = (
    <Alert
      type="info"
      showIcon
      data-testid="waiting-state"
      className={props.className}
      message="대기 중"
      description={
        <Space direction="vertical" size="small">
          <Paragraph style={{ marginBottom: 0 }}>{props.waitingReason}</Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>필요</strong>: {props.required}
          </Paragraph>
          {props.onNext ? <Button onClick={props.onNext}>{label}</Button> : null}
        </Space>
      }
    />
  );
  if (props['data-testid']) {
    return <div data-testid={props['data-testid']}>{alert}</div>;
  }
  return alert;
}

export interface PageGrammarSummaryProps {
  grammar: PageGrammarPayloadV0;
  'data-testid'?: string;
}

export function PageGrammarSummary(props: PageGrammarSummaryProps): React.ReactElement {
  if (!isCompletePageGrammar(props.grammar)) {
    throw new Error('PageGrammarSummary requires all seven grammar answers');
  }
  const g = props.grammar;
  return (
    <section
      data-testid={props['data-testid'] ?? 'page-grammar-summary'}
      aria-label="페이지 문법 요약"
    >
      <Paragraph>
        <strong>위치</strong>: {g.whereAmI}
      </Paragraph>
      <Paragraph>
        <strong>대상</strong>: {g.whatIsThis}
      </Paragraph>
      <Paragraph>
        <strong>상태</strong>: {g.whatStateIsItIn}
      </Paragraph>
      <Paragraph>
        <strong>책임</strong>: {g.whoIsResponsible}
      </Paragraph>
      <Paragraph>
        <strong>변경</strong>: {g.whatChanged}
      </Paragraph>
      <Paragraph>
        <strong>필요</strong>: {g.whatIsRequired}
      </Paragraph>
      <Paragraph>
        <strong>다음</strong>: {g.whatShouldIDoNext}
      </Paragraph>
    </section>
  );
}

/** Re-export frozen operational primitives (Family A) — do not fork implementations here. */
export {
  ContextBar,
  StatusSummary,
  ResponsibilitySummary,
  PrimaryActionArea,
  OperationTimeline,
  ObjectContextPanel,
  AuthoritySummary,
  EvidenceSummary,
  MutationDiff,
  ReceiptLink,
  RecoveryPanel,
} from './primitives';
export type {
  ContextBarProps,
  StatusSummaryProps,
  ResponsibilitySummaryProps,
  PrimaryActionAreaProps,
  OperationTimelineProps,
  OperationTimelineEntryV0,
  ObjectContextPanelProps,
  AuthoritySummaryProps,
  EvidenceSummaryProps,
  MutationDiffProps,
  MutationDiffRowV0,
  ReceiptLinkProps,
  ServerReceiptRefV0,
  RecoveryPanelProps,
  PrimitivePresentationState,
} from './primitives';

/** Re-export Family B primitives — five previously missing semantic owners. */
export {
  DocumentPanel,
  NextStepPanel,
  TimeContextPanel,
  ApprovalSummary,
  APPROVAL_SUMMARY_DECISION_STATUSES_V0,
  SecondaryActionGroup,
} from './primitives';
export type {
  DocumentPanelEntryV0,
  DocumentPanelProps,
  NextStepEntryV0,
  NextStepPanelProps,
  TimeContextEntryV0,
  TimeContextPanelProps,
  ApprovalSummaryDecisionStatusV0,
  ApprovalDecisionStateV0,
  ApprovalSummaryProps,
  SecondaryActionEntryV0,
  SecondaryActionGroupProps,
} from './primitives';
